import { getPossibleDuplicateClients } from "./api.ts";
import { clientLastVisitText } from "./clientIdentityDisplay.ts";
import { createFailureState } from "./failureState.ts";
import type { Session } from "./session.ts";

export function mountPossibleDuplicateClients(options: {
  element: HTMLElement;
  session: () => Session | undefined;
  back: () => void;
}): { show: () => void; reset: () => void; refreshPermission: () => void } {
  let generation = 0;
  let next: string | undefined;
  let snapshotId: string | undefined;
  let freshness: string | null | undefined;
  const root = options.element;
  const back = document.createElement("button");
  back.type = "button";
  back.className = "btn-secondary";
  back.textContent = "Shift and closeout";
  back.addEventListener("click", options.back);
  const heading = document.createElement("h1");
  heading.textContent = "Possible duplicates";
  const status = document.createElement("p");
  status.setAttribute("role", "status");
  const asOf = document.createElement("p");
  asOf.className = "admin-page-subtitle";
  // UIFIX2 (SF-10): a failed load shows the shared failure state in place of the table, never raw server text.
  const failure = document.createElement("div");
  failure.hidden = true;
  const wrap = document.createElement("div");
  wrap.className = "cli-duplicates-table-wrap";
  const table = document.createElement("table");
  table.className = "cli-duplicates-table";
  const head = document.createElement("thead");
  const headers = document.createElement("tr");
  for (const label of [
    "Name",
    "Phone",
    "Preferred shop",
    "Last visit",
    "Membership",
    "MyTime client id",
  ]) {
    const cell = document.createElement("th");
    cell.scope = "col";
    cell.textContent = label;
    headers.append(cell);
  }
  head.append(headers);
  const body = document.createElement("tbody");
  table.append(head, body);
  wrap.append(table);
  const more = document.createElement("button");
  more.type = "button";
  more.className = "btn-secondary";
  more.textContent = "Load more";
  more.hidden = true;
  const reload = document.createElement("button");
  reload.type = "button";
  reload.className = "btn-secondary";
  reload.textContent = "Reload";
  root.append(back, heading, asOf, status, failure, wrap, more, reload);
  const reset = (): void => {
    generation++;
    next = undefined;
    snapshotId = undefined;
    freshness = undefined;
    body.replaceChildren();
    status.textContent = "";
    asOf.textContent = "";
    more.hidden = true;
    clearFailure();
  };
  const clearFailure = (): void => {
    failure.replaceChildren();
    failure.hidden = true;
    wrap.hidden = false;
    reload.hidden = false;
  };
  const showFailure = (sentence: string, canRetry: boolean): void => {
    status.textContent = "";
    failure.replaceChildren(
      createFailureState({ sentence, onRetry: canRetry ? () => show() : undefined, testid: "possible-duplicates-failure" }),
    );
    failure.hidden = false;
    wrap.hidden = true;
    more.hidden = true;
    reload.hidden = true;
  };
  const load = async (): Promise<void> => {
    const session = options.session();
    if (session === undefined) {
      reset();
      status.textContent = "Access unavailable";
      return;
    }
    if (root.hidden) return;
    const sequence = ++generation;
    more.disabled = true;
    reload.disabled = true;
    clearFailure();
    status.textContent = "Loading…";
    const result = await getPossibleDuplicateClients(
      session.apiBaseUrl,
      session.tenantId,
      next,
      snapshotId,
    );
    if (sequence !== generation || root.hidden) return;
    if (options.session()?.token !== session.token) {
      reset();
      return;
    }
    more.disabled = false;
    reload.disabled = false;
    if (result.status !== 200) {
      const refused = result.status === 403 || result.status === 401;
      if (refused) reset();
      showFailure(
        refused ? "You do not have access to possible duplicates." : "Possible duplicates could not load.",
        !refused,
      );
      return;
    }
    if (freshness !== undefined && freshness !== result.body.freshness.lastSyncedAt) {
      reset();
      status.textContent = "Directory updated. Reload.";
      return;
    }
    if (!result.body.available) {
      status.textContent = "Duplicate check not yet available";
      more.hidden = true;
      return;
    }
    snapshotId = result.body.snapshotId ?? undefined;
    freshness = result.body.freshness.lastSyncedAt;
    if (freshness !== null) {
      const at = new Date(freshness);
      asOf.textContent = Number.isNaN(at.getTime())
        ? ""
        : `Client directory as of ${at.toLocaleString()}`;
    } else asOf.textContent = "Client directory not yet available";
    for (const client of result.body.results) {
      const row = document.createElement("tr");
      for (const text of [
        client.displayName,
        client.phoneLastFour ? `•••• ${client.phoneLastFour}` : "",
        client.originLocationName ?? "",
        clientLastVisitText(client),
        client.membershipActive ? "Member" : "Not member",
        client.id,
      ]) {
        const cell = document.createElement("td");
        cell.textContent = text;
        row.append(cell);
      }
      body.append(row);
    }
    status.textContent = body.childElementCount === 0 ? "No possible duplicates" : "";
    next = result.body.nextAfterClientId ?? undefined;
    more.hidden = next === undefined;
  };
  const show = (): void => {
    reset();
    more.disabled = false;
    reload.disabled = false;
    void load();
  };
  more.addEventListener("click", () => void load());
  reload.addEventListener("click", show);
  return {
    show,
    reset,
    refreshPermission: () => {
      if (options.session() === undefined) reset();
    },
  };
}
